import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import PersonIcon from '@material-ui/icons/Person';
import SchoolIcon from '@material-ui/icons/School';
import CardIcon from '../../../assets/icons/cards.svg';
import Dashboard from '../../../assets/icons/dashboard.svg';
import SessionIcon from '../../../assets/icons/sessions.svg';
import RepositoryScanIcon from '../../../assets/icons/repositoryScanIcon.svg';
import { ReactComponent as PersonIconTick } from '../../../assets/icons/personIconTick.svg';
import { useStyles } from './Sidebar.styles';
import MoreHoriz from '@material-ui/icons/MoreHoriz';
import SidebarList, { assessmentTypes } from '../../SidebarList/SidebarList.component';
import Popover from '@material-ui/core/Popover';
import ProcessesList from './../../ProcessesList/ProcessesList.component';
import { viewType } from './../Layout.types';
import useSessionUser from '../../../hooks/useSessionUser';
import { ExaminerFunction } from '../../../pages/Assessment/Assessment.types';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import usePermission, { UserPermission } from '../../../hooks/usePermission';
import { ReactComponent as CandidatesListIcon } from '../../../assets/icons/candidatesListIcon.svg';
import { useTranslation } from 'react-i18next';
import { leaveAssessmentSession } from '../../../Context/SignalR.context';
import { Paths } from '../../../utils/routerUrls';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import TooltipOneComponent from '../../TooltipOne/TooltipOne.component';
import { setAssessmentSessionEvent } from '../../../services/scoring/scoring.service';
import { needToNotifyAboutLeaveAssessmentProcess } from '../../../utils/assessmentSessionProcess';
import { useExaminer } from '../../../Context/Examiner.context';

interface SidebarItem {
  icon: React.ReactElement;
  text: string;
  link: string;
  hasPermission: boolean;
  onClick?: () => void;
}

interface SidebarProps {
  viewType?: viewType;
  isMinimized?: boolean;
  setIsMinimized?: (value: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ viewType, isMinimized, setIsMinimized }) => {
  const classes = useStyles();
  const assessmentSession = useSelector((state: RootState) => state.assessmentSession.assessmentSession);
  const { userAssessmentSessionInfo } = useSessionUser();
  const { t } = useTranslation();
  const { setExaminer } = useExaminer();

  const userFunction = userAssessmentSessionInfo.function;
  const sideBarType = !isMinimized ? classes.sidebar : classes.smallSideBar;
  const [popoverAnchor, setPopoverAnchor] = useState<SVGSVGElement | null>(null);

  const showPopover = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    setPopoverAnchor(event.currentTarget);
  };
  const closePopover = () => {
    setPopoverAnchor(null);
  };

  const listItems: SidebarItem[] = [
    {
      icon: (
        <PersonIcon className={classes.sidebarMenuIcon} titleAccess={isMinimized ? t('global:sidebar.users') : ''} />
      ),
      text: t('global:sidebar.users'),
      link: Paths.users,
      hasPermission:
        viewType === 'admin' &&
        (usePermission(UserPermission.AdminUserRead) || usePermission(UserPermission.AdminOkeUserRead)),
    },
    {
      icon: (
        <PersonIconTick
          style={{
            width: '20px',
            height: '19px',
            marginRight: 10,
            marginLeft: 4,
            paddingRight: '8px',
            pointerEvents: 'none',
          }}
          title={isMinimized ? t('global:sidebar.roles') : ''}
        />
      ),
      text: t('global:sidebar.roles'),
      link: Paths.roles,
      hasPermission: viewType === 'admin' && usePermission(UserPermission.AdminRolePermissionsRead),
    },
    {
      icon: (
        <SchoolIcon
          className={classes.sidebarMenuIcon}
          titleAccess={isMinimized ? t('global:sidebar.examiners') : ''}
        />
      ),
      text: t('global:sidebar.examiners'),
      link: Paths.examiners,
      hasPermission:
        viewType === 'admin' &&
        (usePermission(UserPermission.AdminExaminerRead) || usePermission(UserPermission.AdminOkeExaminerRead)),
    },
    {
      icon: (
        <img className={classes.sidebarCardIcon} src={CardIcon} alt={isMinimized ? t('global:sidebar.cards') : ''} />
      ),
      text: t('global:sidebar.cards'),
      link: Paths.cards,
      hasPermission:
        viewType === 'admin' &&
        (usePermission(UserPermission.AdminTasksSolutionSheetRead) ||
          usePermission(UserPermission.AdminOkeTaskSolutionSheetRead)),
    },
    {
      icon: (
        <img
          className={classes.sidebarCardIcon}
          src={SessionIcon}
          alt={isMinimized ? t('global:sidebar.sessions') : ''}
        />
      ),
      text: t('global:sidebar.sessions'),
      link: Paths.sessions,
      hasPermission: viewType === 'admin' && usePermission(UserPermission.AdminSessionRead),
    },
    {
      icon: <img className={classes.sidebarMenuIcon} src={Dashboard} alt={isMinimized ? 'Sesje oceniania' : ''} />,
      text: 'Sesje oceniania',
      link: Paths.dashboard,
      hasPermission: viewType !== undefined && ['examiner', 'dashboard', 'sampleExaminer'].includes(viewType),
      onClick: () => {
        if (needToNotifyAboutLeaveAssessmentProcess(assessmentSession, userFunction)) {
          setAssessmentSessionEvent(2);
        }
        setExaminer(undefined);
        leaveAssessmentSession(assessmentSession);
        sessionStorage.removeItem('startWork');
      },
    },
    {
      icon: (
        <img
          className={classes.sidebarMenuIconScan}
          src={RepositoryScanIcon}
          alt={isMinimized ? t('global:sidebar.sampleAssessmentRepository') : ''}
        />
      ),
      text: t('global:sidebar.sampleAssessmentRepository'),
      link: Paths.sampleAssessmentRepository,
      hasPermission: viewType === 'sampleExaminer' && userFunction !== ExaminerFunction.KK,
    },
    {
      icon: (
        <CandidatesListIcon
          style={{ marginLeft: 2 }}
          className={classes.sidebarMenuIcon}
          title={isMinimized ? t('global:sidebar.examinees') : ''}
        />
      ),
      text: t('global:sidebar.examinees'),
      link: Paths.examinees,
      hasPermission:
        viewType === 'admin' &&
        (usePermission(UserPermission.AdminExamineeRead) || usePermission(UserPermission.AdminOkeExamineeRead)),
    },
  ];

  const footer = (
    <>
      <ul>
        <li>
          <TooltipOne title="Ta strona otworzy się w nowej karcie" placement="top">
            <a
              href="https://bip.cke.gov.pl/artykul/206/1393/ochrona-danych-osobowych-rodo"
              target="_blank"
              rel="noreferrer"
              aria-label="Klauzula Rodo. Ta strona otworzy się w nowej karcie"
            >
              Klauzula RODO
            </a>
          </TooltipOne>
        </li>
        <li>
          <TooltipOne title="Ta strona otworzy się w nowej karcie" placement="top">
            <Link
              to={Paths.accessibilityDeclaration}
              target="_blank"
              aria-label="Deklaracja dostępności. Ta strona otworzy się w nowej karcie"
            >
              Deklaracja dostępności
            </Link>
          </TooltipOne>
        </li>
      </ul>
    </>
  );

  return (
    <div className={sideBarType}>
      <div className={classes.sideBarContainer}>
        <ul className={classes.sidebarMenu}>
          {listItems.map((e) => {
            return (
              e.hasPermission &&
              (isMinimized ? (
                <li key={e.text}>
                  <TooltipOne title={e.text} placement="right">
                    <NavLink
                      to={e.link}
                      activeClassName={classes.activeLink}
                      onClick={e.onClick ? e.onClick : undefined}
                    >
                      {e.icon}
                    </NavLink>
                  </TooltipOne>
                </li>
              ) : (
                <li key={e.text}>
                  <NavLink to={e.link} activeClassName={classes.activeLink} onClick={e.onClick ? e.onClick : undefined}>
                    <span className={classes.sideBarText}>
                      {e.icon} {e.text}
                    </span>
                  </NavLink>
                </li>
              ))
            );
          })}
        </ul>

        {viewType === 'examiner' && <ProcessesList isMinimized={isMinimized} />}
        {viewType && ['examiner', 'sampleExaminer'].includes(viewType) && (
          <SidebarList
            isMinimized={isMinimized}
            setIsMinimized={setIsMinimized}
            assessmentType={
              viewType && viewType === 'examiner' ? assessmentTypes.assessment : assessmentTypes.sampleAssassment
            }
          />
        )}
      </div>

      <div className={classes.footer}>
        {!isMinimized && <div className={classes.moreHoriz}>{footer}</div>}
        {isMinimized && (
          <>
            <div className={classes.moreHoriz}>
              <TooltipOne title="Pokaż więcej">
                <div className={classes.moreOnHoverButton}>
                  <MoreHoriz onClick={showPopover} />
                </div>
              </TooltipOne>
            </div>
            <Popover
              open={!!popoverAnchor}
              anchorEl={popoverAnchor}
              onClose={closePopover}
              anchorOrigin={{
                vertical: -70,
                horizontal: 10,
              }}
            >
              <div className={classes.popover}>{footer}</div>
            </Popover>
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(Sidebar);
